import { useEffect, useRef } from "react";

// How often the accumulated active time is checked against the threshold
// while the tab stays in the foreground. Coarse on purpose - this only
// drives a background "mark as read" flag, not a visible timer.
const CHECK_INTERVAL_MS = 5000;

// Accumulates only the time the tab has actually been in the foreground
// (Page Visibility API), and calls `onThreshold` once that active time
// reaches `thresholdMs`. A tab left open in the background contributes
// nothing while hidden.
//
// Resets whenever `key` or `thresholdMs` changes (e.g. navigating to a
// different post without unmounting), and stops entirely while `enabled`
// is false - callers use that to gate on sign-in state and to stop once the
// threshold has already been reached, so `onThreshold` fires at most once
// per `key`/enabled "session".
export function useActiveReadingTime(key: string, thresholdMs: number, enabled: boolean, onThreshold: () => void): void {
    const onThresholdRef = useRef(onThreshold);
    onThresholdRef.current = onThreshold;

    useEffect(() => {
        if (!enabled || thresholdMs <= 0) return undefined;

        let accumulatedMs = 0;
        let visibleSince: number | null = document.visibilityState === "visible" ? Date.now() : null;
        let done = false;

        const activeMs = () => accumulatedMs + (visibleSince !== null ? Date.now() - visibleSince : 0);

        const checkThreshold = () => {
            if (done || activeMs() < thresholdMs) return;
            done = true;
            onThresholdRef.current();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                if (visibleSince !== null) {
                    accumulatedMs += Date.now() - visibleSince;
                    visibleSince = null;
                }
                checkThreshold();
            } else if (visibleSince === null) {
                visibleSince = Date.now();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        const interval = setInterval(checkThreshold, CHECK_INTERVAL_MS);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            clearInterval(interval);
        };
    }, [key, thresholdMs, enabled]);
}
